import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Spinner,
	FormGroup,
	Label,
	Input,
	Row,
	Col,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';
import { updateScheduledActivity } from '../../../../slices/scheduledActivity/thunk';

const getCurrentDatetimeLocal = () => {
	const now = new Date();
	now.setSeconds(0, 0);
	const pad = n => String(n).padStart(2, '0');
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const formatDate = dateString => {
	if (!dateString) return '-';
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return '-';
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

const UploadActivityDocumentModal = ({ isOpen, onClose, activity, onSuccess }) => {
	const dispatch = useDispatch();

	const [file, setFile] = useState(null);
	const [reportDatetime, setReportDatetime] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (isOpen) {
			setFile(null);
			setReportDatetime(getCurrentDatetimeLocal());
			setError(null);
		}
	}, [isOpen]);

	const handleFileChange = e => {
		const selected = e.target.files?.[0] || null;
		setFile(selected);
		setError(null);
	};

	const handleSubmit = async () => {
		if (!file) {
			setError('Debe seleccionar un archivo.');
			return;
		}
		if (!reportDatetime) {
			setError('Debe ingresar la fecha y hora del reporte.');
			return;
		}
		if (!activity?._id) return;

		setIsSubmitting(true);
		setError(null);

		try {
			const formData = new FormData();
			formData.append('files', file);
			const uploadResponse = await uploadFiles({ formData });
			const fileId =
				uploadResponse?.[0]?._id ||
				uploadResponse?.[0]?.id ||
				uploadResponse?.data?.[0]?._id;

			if (!fileId) {
				throw new Error('No se pudo obtener el ID del archivo subido.');
			}

			await dispatch(
				updateScheduledActivity({
					id: activity._id,
					data: {
						work_order_id: activity.work_order_id,
						activity_status_id: activity.activity_status_id,
						reporting_date: activity.reporting_date,
						period: activity.period,
						file_id: fileId,
						report_datetime: new Date(reportDatetime).toISOString(),
					},
				})
			).unwrap();

			if (onSuccess) onSuccess();
			onClose();
		} catch (err) {
			console.error('Error al subir documento:', err);
			setError('Ocurrió un error al subir el documento. Por favor intente nuevamente.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={isSubmitting ? undefined : onClose}
			centered={true}
			size='md'
			backdrop='static'
		>
			<ModalHeader toggle={isSubmitting ? undefined : onClose} className='bg-light p-3'>
				Subir Documento de Actividad
			</ModalHeader>
			<ModalBody>
				<Row className='mb-3'>
					<Col md={6}>
						<Label className='fw-semibold'>Código de Obra</Label>
						<p className='mb-0'>{activity?.work_order?.work_order_code || '-'}</p>
					</Col>
					<Col md={6}>
						<Label className='fw-semibold'>Fecha Reportabilidad</Label>
						<p className='mb-0'>{formatDate(activity?.reporting_date)}</p>
					</Col>
				</Row>

				<FormGroup>
					<Label for='reportDatetime'>
						Fecha y Hora del Reporte <span className='text-danger'>*</span>
					</Label>
					<Input
						id='reportDatetime'
						type='datetime-local'
						value={reportDatetime}
						onChange={e => setReportDatetime(e.target.value)}
						disabled={isSubmitting}
					/>
				</FormGroup>

				<FormGroup>
					<Label for='activityFile'>
						Documento <span className='text-danger'>*</span>
					</Label>
					<Input
						id='activityFile'
						type='file'
						onChange={handleFileChange}
						disabled={isSubmitting}
					/>
					{file && (
						<small className='text-muted mt-1 d-block'>
							Archivo seleccionado: {file.name}
						</small>
					)}
				</FormGroup>

				{error && (
					<div className='alert alert-danger py-2 mb-0'>
						{error}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<Button
					type='button'
					onClick={onClose}
					className='btn-danger btn-label'
					disabled={isSubmitting}
				>
					<i className='ri-close-line label-icon'></i>
					Cancelar
				</Button>
				<Button
					color='primary'
					className='btn btn-label btn-primary'
					onClick={handleSubmit}
					disabled={isSubmitting}
				>
					{isSubmitting ? (
						<i className='label-icon'><Spinner size='sm' /></i>
					) : (
						<i className='ri-upload-2-line label-icon'></i>
					)}
					{isSubmitting ? 'Subiendo...' : 'Subir Documento'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default UploadActivityDocumentModal;
